import { useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import { humanizeLabel } from "../data/alert-queue";
import {
  formatPolicyValueDisplay,
  policyEnumOptions,
  policyKeyHint,
  policyKeyLabel,
  policyStringArrayOptions,
} from "../data/policy-display";
import {
  clearAccountPolicy,
  clearSitePolicyOverride,
  fetchEffectiveSitePolicy,
  formatPolicyValue,
  parsePolicyInput,
  policyDomain,
  SAFE_POLICY_WRITE_FAILURE,
  setAccountPolicy,
  setSitePolicyOverride,
  SOURCE_COPY,
  type EffectivePolicyRow,
} from "../data/policy";
import { fetchRouteRollout } from "../data/rollout";
import { useReadModel } from "../data/useReadModel";

// Policy panel (CONSOLE-POLICY-READ-SURFACE + CONSOLE-POLICY-OVERRIDE-EDITOR). Read side:
// pick a site, the hosted effective_site_policy resolver returns every key's effective
// value + source, rendered as grouped cards with Site override / Account / Default chips.
// Write side (policy.manage ONLY - policy.view users keep zero write affordances): per-row
// Edit opens a typed editor with explicit scoped submits (site override only for
// site_overridable keys; account-wide for all) and source-aware revert controls with
// inheritance-explaining copy. After ANY write the resolver is refetched - the console
// never merges policy layers itself. All four write RPCs are the only write paths
// (direct table writes are revoked server-side). Fail closed throughout.

interface SiteOption {
  siteId: string;
  siteName: string;
}

export function PolicyReadPanel({
  client,
  accountId,
  canView = false,
  canManage = false,
  hiddenDomains = [],
}: {
  client: SupabaseClient;
  accountId: string;
  /** policy.view capability - fail closed. */
  canView?: boolean;
  /** policy.manage capability (admin-only) - write affordances only exist when true. */
  canManage?: boolean;
  /** Product modules may remove an entire policy domain from the Admin surface. */
  hiddenDomains?: readonly string[];
}) {
  if (!canView) {
    return null;
  }
  return (
    <PolicyReadPanelInner
      client={client}
      accountId={accountId}
      canManage={canManage}
      hiddenDomains={hiddenDomains}
    />
  );
}

/** Split so data hooks only mount (and read) behind the canView gate. */
function PolicyReadPanelInner({
  client,
  accountId,
  canManage,
  hiddenDomains,
}: {
  client: SupabaseClient;
  accountId: string;
  canManage: boolean;
  hiddenDomains: readonly string[];
}) {
  const sites = useReadModel<SiteOption[]>(
    async () => {
      const routes = await fetchRouteRollout(client, accountId);
      const bySite = new Map<string, SiteOption>();
      for (const route of routes) {
        if (route.siteId !== null && !bySite.has(route.siteId)) {
          bySite.set(route.siteId, { siteId: route.siteId, siteName: route.siteName ?? "Site" });
        }
      }
      return [...bySite.values()].sort((a, b) => a.siteName.localeCompare(b.siteName));
    },
    [client, accountId],
  );
  const [siteId, setSiteId] = useState<string>("");
  const [rows, setRows] = useState<EffectivePolicyRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [resolveNonce, setResolveNonce] = useState(0);

  useEffect(() => {
    if (siteId === "") {
      setRows(null);
      setLoadError(null);
      return;
    }
    let active = true;
    setLoadError(null);
    fetchEffectiveSitePolicy(client, accountId, siteId)
      .then((loaded) => {
        if (active) setRows(loaded);
      })
      .catch((caught: unknown) => {
        if (active) setLoadError(caught instanceof Error ? caught.message : "Could not load site policies.");
      });
    return () => {
      active = false;
    };
  }, [client, accountId, siteId, resolveNonce]);

  if (sites.status === "loading" && sites.data === null) {
    return <p className="surface__state" role="status">Loading sites...</p>;
  }
  if (sites.status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {sites.error ?? "Could not load sites."}
      </p>
    );
  }

  const siteOptions = sites.data ?? [];
  const hiddenDomainSet = new Set(hiddenDomains);
  const domains = new Map<string, EffectivePolicyRow[]>();
  for (const row of rows ?? []) {
    const domain = policyDomain(row.key);
    if (hiddenDomainSet.has(domain)) {
      continue;
    }
    const bucket = domains.get(domain);
    if (bucket === undefined) {
      domains.set(domain, [row]);
    } else {
      bucket.push(row);
    }
  }

  return (
    <div className="policy">
      <div className="policy__intro">
        <label className="tpl__field policy__picker">
          Preview policies for site
          <select
            value={siteId}
            onChange={(event) => {
              setSiteId(event.target.value);
              setRows(null);
              setEditingKey(null);
            }}
          >
            <option value="">Choose a site...</option>
            {siteOptions.map((site) => (
              <option key={site.siteId} value={site.siteId}>
                {site.siteName}
              </option>
            ))}
          </select>
        </label>
        <p className="policy__scope-note">
          This picker only affects <strong>site policies</strong> below: capture, alerts, scheduling,
          and timeclock. {hiddenDomainSet.has("branding")
            ? "Privacy rows are"
            : "Branding and privacy rows are"} account-wide even when you preview a site.
          Duty tracking, mobile flags, consent, and members are separate account-wide settings.
        </p>
        {siteId !== "" ? (
          <p className="policy__site-banner" role="status">
            Showing effective policies for{" "}
            <strong>{siteOptions.find((site) => site.siteId === siteId)?.siteName ?? "this site"}</strong>
          </p>
        ) : null}
      </div>
      {siteOptions.length === 0 ? (
        <p className="surface__state" role="status">
          No sites yet - sites appear here once routes are configured.
        </p>
      ) : null}
      {loadError !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {loadError}
        </p>
      ) : siteId !== "" && rows === null ? (
        <p className="surface__state" role="status">
          Resolving policies...
        </p>
      ) : null}
      {[...domains.entries()].map(([domain, domainRows]) => (
        <section key={domain} className="policy__group" role="region" aria-label={`${domain} policies`}>
          <h3 className="policy__domain">{humanizeLabel(domain)}</h3>
          <ul className="policy__rows">
            {domainRows.map((row) => {
              const hint = policyKeyHint(row.key);
              return (
              <li key={row.key} className="policy__row-wrap">
                <div className="policy__row">
                  <div className="policy__meta">
                    <span className="policy__key">{policyKeyLabel(row.key)}</span>
                    {hint !== null ? <span className="policy__hint">{hint}</span> : null}
                  </div>
                  <span className="policy__value">{formatPolicyValueDisplay(row.key, row.valueType, row.effectiveValue)}</span>
                  <span className={`schedule__chip policy__source policy__source--${row.source}`}>
                    {SOURCE_COPY[row.source]}
                  </span>
                  {row.scope === "account_only" ? (
                    <span className="schedule__chip schedule__chip--credential">Account-wide</span>
                  ) : null}
                  {canManage ? (
                    <button
                      type="button"
                      className="builder__navbtn policy__edit"
                      onClick={() => setEditingKey(editingKey === row.key ? null : row.key)}
                    >
                      Edit
                    </button>
                  ) : null}
                </div>
                {canManage && editingKey === row.key ? (
                  <PolicyEditor
                    client={client}
                    accountId={accountId}
                    siteId={siteId}
                    row={row}
                    onClose={() => setEditingKey(null)}
                    onSaved={() => {
                      setEditingKey(null);
                      setResolveNonce((nonce) => nonce + 1);
                    }}
                  />
                ) : null}
              </li>
            );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

/**
 * Typed per-key editor. Explicit scoped submits only; client-side parse rejects garbage
 * inline with zero writes; server failures render allowlisted-or-safe copy. Saving or
 * reverting refetches the resolver behind the receipt.
 */
function PolicyEditor({
  client,
  accountId,
  siteId,
  row,
  onClose,
  onSaved,
}: {
  client: SupabaseClient;
  accountId: string;
  siteId: string;
  row: EffectivePolicyRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [raw, setRaw] = useState(() => formatPolicyValue(row.effectiveValue));
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const label = policyKeyLabel(row.key);
  const enumOptions = row.valueType === "string"
    ? policyEnumOptions(row.key, row.effectiveValue)
    : null;
  const stringArrayOptions = row.valueType === "string_array"
    ? policyStringArrayOptions(row.key, row.effectiveValue)
    : [];
  const selectedStringArrayValues = readStringArray(raw);

  function updateRaw(next: string): void {
    setRaw(next);
    setFailure(null);
  }

  function updateStringArrayValue(value: string, checked: boolean): void {
    const nextValues = new Set(selectedStringArrayValues);
    if (checked) {
      nextValues.add(value);
    } else {
      nextValues.delete(value);
    }
    updateRaw(JSON.stringify(
      stringArrayOptions
        .filter((option) => nextValues.has(option.value))
        .map((option) => option.value),
    ));
  }

  async function run(action: () => Promise<{ ok: true } | { ok: false; reason: string }>): Promise<void> {
    if (busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const outcome = await action();
      if (outcome.ok) {
        onSaved();
      } else {
        setFailure(outcome.reason);
      }
    } catch {
      setFailure(SAFE_POLICY_WRITE_FAILURE);
    } finally {
      setBusy(false);
    }
  }

  function submitValue(scope: "site" | "account"): void {
    const parsed = parsePolicyInput(row.valueType, raw);
    if (!parsed.ok) {
      setFailure(parsed.message);
      return;
    }
    void run(() =>
      scope === "site"
        ? setSitePolicyOverride(client, accountId, siteId, row.key, parsed.value)
        : setAccountPolicy(client, accountId, row.key, parsed.value),
    );
  }

  return (
    <form
      className="policy__editor"
      aria-label={`Edit policy - ${row.key}`}
      onSubmit={(event) => event.preventDefault()}
    >
      {row.valueType === "string_array" ? (
        <fieldset className="tpl__fieldset policy__editor-value" disabled={busy}>
          <legend className="tpl__legend">{label}</legend>
          {stringArrayOptions.length > 0 ? (
            <div className="tpl__weekdays">
              {stringArrayOptions.map((option) => (
                <label key={option.value}>
                  <input
                    type="checkbox"
                    checked={selectedStringArrayValues.includes(option.value)}
                    onChange={(event) => updateStringArrayValue(option.value, event.target.checked)}
                  />{" "}
                  {option.label}
                </label>
              ))}
            </div>
          ) : (
            <p className="surface__state">No choices are configured for this policy.</p>
          )}
        </fieldset>
      ) : (
        <label className="tpl__field policy__editor-value">
          {label}
          {row.valueType === "boolean" ? (
            <select disabled={busy} value={raw} onChange={(event) => updateRaw(event.target.value)}>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          ) : enumOptions !== null ? (
            <select disabled={busy} value={raw} onChange={(event) => updateRaw(event.target.value)}>
              {enumOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : (
            <input
              type={row.valueType === "integer" ? "number" : "text"}
              disabled={busy}
              value={raw}
              onChange={(event) => updateRaw(event.target.value)}
            />
          )}
        </label>
      )}
      <div className="policy__editor-actions">
        {row.scope !== "account_only" ? (
          <button type="button" className="minter__confirm-go" disabled={busy} onClick={() => submitValue("site")}>
            Set site override
          </button>
        ) : null}
        <button type="button" className="minter__confirm-go" disabled={busy} onClick={() => submitValue("account")}>
          Set account-wide
        </button>
        {row.source === "site_override" ? (
          <button
            type="button"
            className="builder__navbtn"
            disabled={busy}
            title="This site goes back to inheriting the account value or shipped default"
            onClick={() => void run(() => clearSitePolicyOverride(client, accountId, siteId, row.key))}
          >
            Remove site override
          </button>
        ) : null}
        {row.source === "account_policy" ? (
          <button
            type="button"
            className="builder__navbtn"
            disabled={busy}
            title="All sites without their own override go back to the shipped default"
            onClick={() => void run(() => clearAccountPolicy(client, accountId, row.key))}
          >
            Clear account value
          </button>
        ) : null}
        <button type="button" className="builder__navbtn" disabled={busy} onClick={onClose}>
          Cancel
        </button>
      </div>
      {failure !== null ? (
        <p className="surface__state surface__state--error" role="alert">
          {failure}
        </p>
      ) : null}
    </form>
  );
}

function readStringArray(raw: string): string[] {
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((value): value is string => typeof value === "string")
      : [];
  } catch {
    return [];
  }
}
